import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { useSearchParams } from 'react-router';
import { track } from '@/lib/analytics';
import { absoluteUrl } from '@/lib/config';
import { interpolate } from '@/lib/i18n';
import { roundCompare } from './compare';
import { content } from './content';
import type { VisualizerCopy } from './content';
import { NEUTRAL_STONE_TINT, NEUTRAL_STONE_URL } from './neutral-stone';
import { getScene } from './scenes';
import type { SceneDefinition, SceneId } from './scenes';
import type { StageStatus, StageTexture } from './Stage';
import { TextureProbe } from './TextureProbe';
import { snapScale, parseVisualizerSearch, serializeVisualizerState } from './url-state';
import type { Rotation, VisualizerState } from './url-state';
import { useCommittedValue } from './useCommittedValue';
import { useStoneSelection, useTextureState } from './useStone';
import type { StoneSelection } from './useStone';

const SAMPLE_TEXTURE: StageTexture = { key: 'sample-stone', url: NEUTRAL_STONE_URL };

export interface VisualizerModel {
  copy: VisualizerCopy;
  scene: SceneDefinition;
  selectScene: (id: SceneId) => void;
  /** Live while the slider moves; the URL follows once the hand is still. */
  scale: number;
  setScale: (value: number, immediate?: boolean) => void;
  rotate: Rotation;
  setRotate: (value: Rotation) => void;
  seams: boolean;
  setSeams: (value: boolean) => void;
  /**
   * The before / after divider. Only its committed value lives here — the live one belongs to the stage
   * section, so dragging it re-renders the picture and nothing else.
   */
  compare: { committed: number; commit: (value: number) => void };
  dirty: boolean;
  reset: () => void;
  selection: StoneSelection;
  selectStone: (slug: string, name: string) => void;
  /** The stone that is on screen right now (not the one that is loading). */
  shownName: string;
  stage: {
    status: StageStatus;
    texture: StageTexture | null;
    /** The parts of the picture's description that do not change while the divider moves. */
    label: { scene: string; stone: string; surface: string; scale: number; rotate: number };
  };
  /** Render this somewhere inside the page: it loads the texture. */
  probe: ReactElement | null;
  /** The address of exactly this view, for sharing. */
  link: string;
  /** Slug and details of the stone to enquire about; `null` when only a sample is on screen. */
  enquiry: { slug: string; name: string; sku: string } | null;
  /** Average colour of the stone, for flat thumbnails. */
  tint: string;
  announcement: { text: string; n: number };
  announce: (message: string) => void;
}

/**
 * All the state of the visualizer in one place. The URL is the single source of truth for what the
 * view is (`?scene=&product=&scale=&rotate=&compare=`); changes are written back with `replace` — no
 * history entries — and without resetting the scroll position. Continuous controls (scale, the compare
 * divider) keep a live local value while they move and write the URL when they settle.
 */
export function useVisualizer(): VisualizerModel {
  const copy = content;
  const [params, setParams] = useSearchParams();
  const url = useMemo(() => parseVisualizerSearch(params), [params]);

  // `update` composes with the latest write, so two changes in one tick never overwrite each other.
  const latest = useRef<VisualizerState>(url);
  useEffect(() => {
    latest.current = url;
  }, [url]);
  // analytics (consent-gated, closed list): the first change a visitor makes counts as "used", once per visit
  const reportedUse = useRef(false);
  const update = useCallback(
    (patch: Partial<VisualizerState>) => {
      const next = { ...latest.current, ...patch };
      latest.current = next;
      if (!reportedUse.current) {
        reportedUse.current = true;
        track('visualizer_used', {
          scene: next.scene,
          ...(next.product ? { product: next.product } : {}),
        });
      }
      setParams(serializeVisualizerState(next), { replace: true, preventScrollReset: true });
    },
    [setParams],
  );

  const scene = getScene(url.scene);
  const [scale, setScale] = useCommittedValue(url.scale, (value) => {
    update({ scale: snapScale(value) });
  });
  const commitCompare = useCallback(
    (value: number) => {
      update({ compare: roundCompare(value) });
    },
    [update],
  );

  // The stone: which one, its texture, and what is actually on screen while the next one loads.
  const selection = useStoneSelection(url.product);
  const detail = selection.detail;
  const texture = useTextureState(selection.image, detail ? { name: detail.name } : null);
  const sample = selection.status === 'unavailable' || texture.failed;
  const resolved = texture.resolved;
  const status: StageStatus = sample ? 'sample' : resolved ? 'ready' : 'loading';
  const stageTexture: StageTexture | null = useMemo(
    () => (sample ? SAMPLE_TEXTURE : resolved ? { key: resolved.key, url: resolved.url } : null),
    [sample, resolved],
  );
  const shownName = sample ? copy.stone.sampleName : resolved ? resolved.name : '';

  const probe =
    selection.image && !sample ? (
      <TextureProbe
        key={selection.image.id}
        image={selection.image}
        onResolve={texture.resolve}
        onError={texture.fail}
      />
    ) : null;

  const [announcement, setAnnouncement] = useState({ text: '', n: 0 });
  const announce = useCallback((message: string) => {
    setAnnouncement((previous) => ({ text: message, n: previous.n + 1 }));
  }, []);

  const selectScene = useCallback(
    (id: SceneId) => {
      update({ scene: id });
      announce(interpolate(copy.announce.scene, { scene: copy.scene.options[id].name }));
    },
    [update, announce, copy],
  );
  const selectStone = useCallback(
    (slug: string, name: string) => {
      update({ product: slug });
      announce(interpolate(copy.announce.stone, { stone: name }));
    },
    [update, announce, copy],
  );
  const setRotate = useCallback(
    (value: Rotation) => {
      update({ rotate: value });
    },
    [update],
  );
  const setSeams = useCallback(
    (value: boolean) => {
      update({ seams: value });
    },
    [update],
  );
  const reset = useCallback(() => {
    setScale(1, true);
    update({ rotate: 0, seams: false });
  }, [setScale, update]);

  // The shared link is the committed view: by the time anyone reaches the "Copy link" button, the
  // sliders have been released (or blurred) and written themselves to the address.
  const link = useMemo(
    () =>
      absoluteUrl(
        `/visualizer?${serializeVisualizerState(url, { explicit: true, product: selection.slug })}`,
      ),
    [url, selection.slug],
  );

  const enquiry = useMemo(
    () =>
      detail && !sample
        ? {
            slug: detail.slug,
            name: detail.name,
            sku: detail.sku,
          }
        : null,
    [detail, sample],
  );
  const tint = selection.image?.dominantColor ?? detail?.cover?.dominantColor ?? NEUTRAL_STONE_TINT;

  return {
    copy,
    scene,
    selectScene,
    scale,
    setScale,
    rotate: url.rotate,
    setRotate,
    seams: url.seams,
    setSeams,
    compare: { committed: url.compare, commit: commitCompare },
    dirty: scale !== 1 || url.rotate !== 0 || url.seams,
    reset,
    selection,
    selectStone,
    shownName,
    stage: {
      status,
      texture: stageTexture,
      label: {
        scene: copy.scene.options[scene.id].label,
        stone: shownName || copy.stage.aGranite,
        surface: copy.surfaces[scene.surface],
        scale: Math.round(scale * 100),
        rotate: url.rotate,
      },
    },
    probe,
    link,
    enquiry,
    tint,
    announcement,
    announce,
  };
}
